import { usePageTitle } from '@/hooks/usePageTitle';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useQuoteStore } from '@/store/quoteStore';
import { useBlockStore } from '@/store/blockStore';
import QuoteProductNotice from '@/components/products/QuoteProductNotice';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ErrorReportCard } from '@/components/ErrorReportPanel';
import { PDFEditor } from '@/components/editor';
import PageContainer from '@/components/layout/PageContainer';
import type { TemplateKind } from '@/types';

/** `quoteId` overschrijft de route-parameter; de template-editor geeft zo zijn werkkopie door. */
function QuoteEditorPage({ quoteId, templateKind }: { quoteId?: string; templateKind?: TemplateKind } = {}) {
  const params = useParams<{ id: string }>();
  const id = quoteId ?? params.id;
  const { currentQuote, quoteLoading, quoteError, fetchQuote } = useQuoteStore();
  const loadedQuote = currentQuote && currentQuote.id === id ? currentQuote : null;
  usePageTitle(
    loadedQuote?.templateId ? (templateKind === 'page' ? 'Pagina bewerken' : 'Template bewerken') : 'Offerte bewerken',
    loadedQuote?.title
  );
  const { setBlocks } = useBlockStore();

  /**
   * Mount de editor pas nadat zijn eigen blokken in de globale block-store staan.
   * Anders initialiseert TipTap bij een routewissel kort met de vorige offerte en
   * kan die oude documentstate actief blijven.
   */
  const [blocksHydratedForQuoteId, setBlocksHydratedForQuoteId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      useQuoteStore.getState().setCurrentQuote(null);
    };
  }, []);

  useEffect(() => {
    setBlocksHydratedForQuoteId(null);
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchQuote(id);
    }
  }, [id, fetchQuote]);

  useEffect(() => {
    if (!id || quoteLoading || !currentQuote || currentQuote.id !== id) return;
    if (blocksHydratedForQuoteId === id) return;
    if (!currentQuote.blocks) return;
    setBlocks(currentQuote.blocks);
    setBlocksHydratedForQuoteId(id);
  }, [id, quoteLoading, currentQuote, blocksHydratedForQuoteId, setBlocks]);

  if (quoteLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-100">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (quoteError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-100 gap-2">
        <h2 className="text-xl font-semibold text-destructive">Fout bij laden</h2>
        <p className="text-muted-foreground">{quoteError.message}</p>
        {quoteError.report && <ErrorReportCard report={quoteError.report} className="mt-4" />}
      </div>
    );
  }

  if (!currentQuote) {
    return (
      <PageContainer>
        <h2 className="text-xl font-semibold">Offerte niet gevonden</h2>
      </PageContainer>
    );
  }

  if (!id || currentQuote.id !== id || blocksHydratedForQuoteId !== id) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-100">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ErrorBoundary level="page">
      <QuoteProductNotice quoteId={currentQuote.id} />
      <PDFEditor key={currentQuote.id} quote={currentQuote} templateKind={templateKind} />
    </ErrorBoundary>
  );
}

export default QuoteEditorPage;
